import { useCallback, useMemo, useState } from "react";
import {
  RegisterOptions,
  SubmitHandler,
  useForm,
  useFormState,
  useWatch,
} from "react-hook-form";

import {
  Field,
  FieldHelper,
  FieldLabel,
  FormCard,
  FormPanel,
  LabelRow,
} from "../../../../components/FormPanel";
import {
  FormInput,
  FormMoneyInput,
  InputDate,
  InstrumentSearch,
  SelectOptionTypes,
} from "../../../../components/Input";
import { buildDraftDetails } from "../../../../components/PopUp/confirmShared";
import { localToday } from "../../../../utils";
import { MobileHoldingForm } from "./MobileHoldingForm";
import {
  AMOUNT_HELPER,
  createDefaultHolding,
  FEE_HELPER,
  FORM_ID,
  QUANTITY_HELPER,
} from "./constants";
import { CashbackFundingFields } from "../CashbackFundingFields";
import { CASHBACK_BUY_AMOUNT_HELPER } from "../constants";
import { ChargeField } from "../ChargeField";
import { NoteField } from "../NoteField";
import {
  tradeDateFloor,
  cashbackCappedAmountRules,
  validateQuantity,
  validateTradeDate,
} from "../tradeValidation";
import {
  centsOf,
  settlementNetCentsOf,
  settlementNoteOf,
} from "../settlementNote";
import { useCashbackFunding } from "../useCashbackFunding";
import { HoldingFormValues, HoldingPanelProps } from "./types";

// Buy panel for a ticker-based holding — there is no "create holding" endpoint, a buy *is* the creation (one transaction: holding, cash events, investment patch, snapshot recalc).
// Panel owns the form but never calls the mutation — the page does, via `handleOnSubmit`. `onSubmit` returns that promise so RHF's `isSubmitting` gates the button and close affordance while it's in flight.
export const HoldingPanel = ({
  isOpen,
  onClose,
  investmentName,
  investmentStartDate,
  investmentAccountId,
  settlementAccountName,
  handleOnSubmit,
}: HoldingPanelProps) => {
  const [panelError, setPanelError] = useState<string | null>(null);

  const form = useForm<HoldingFormValues>({
    defaultValues: createDefaultHolding(),
    mode: "onBlur",
    reValidateMode: "onChange",
  });
  const { control, getValues, setError, setValue } = form;

  const { isSubmitting } = useFormState({ control });
  const [amountEur, feeEur, instrument, date, cashbackEur, cashbackSource] =
    useWatch({
      control,
      name: [
        "amountEur",
        "feeEur",
        "instrument",
        "date",
        "cashbackEur",
        "cashbackSource",
      ],
    });
  const settlementNote = settlementNoteOf({
    accountName: settlementAccountName,
    netCents: settlementNetCentsOf({
      amountCents: centsOf(amountEur),
      chargeCents: centsOf(feeEur),
      direction: "OUT",
      cashbackCents: centsOf(cashbackEur),
    }),
    isCashbackFunded: centsOf(cashbackEur) > 0,
  });

  const setCashbackSource = useCallback(
    (option: SelectOptionTypes | null) => setValue("cashbackSource", option),
    [setValue],
  );
  const cashback = useCashbackFunding({
    isEnabled: true,
    investmentAccountId,
    cashbackSource,
    setCashbackSource,
  });

  const minDate = useMemo(
    () => tradeDateFloor(investmentStartDate),
    [investmentStartDate],
  );

  const onSubmit: SubmitHandler<HoldingFormValues> = (data) => {
    setPanelError(null);
    return handleOnSubmit(data, { setError, setPanelError });
  };

  const buildDiscardDetails = () => {
    const values = getValues();
    return buildDraftDetails({
      noun: "holding",
      fieldValues: [
        values.instrument?.symbol,
        values.quantity,
        values.amountEur,
        values.note,
      ],
      primaryLabel: values.instrument?.symbol,
    });
  };

  const dateRules: RegisterOptions<HoldingFormValues, "date"> = {
    required: "Date is required!",
    validate: (value: Date) => validateTradeDate(value, minDate),
  };

  return (
    <FormPanel
      open={isOpen}
      onClose={onClose}
      error={panelError}
      ariaLabel="Add holding form"
      eyebrow="NEW HOLDING"
      title={`Add holding to ${investmentName}`}
      form={form}
      formId={FORM_ID}
      onSubmit={onSubmit}
      submitLabel="Buy holding"
      isSubmitDisabled={!instrument}
      footerNote={settlementNote}
      discard={{
        title: "Cancel this purchase?",
        confirmLabel: "Discard",
        details: buildDiscardDetails,
      }}
      mobile={{
        title: "Add holding",
        submitLabel: "Buy",
        fields: (
          <MobileHoldingForm
            control={control}
            minDate={minDate}
            dateRules={dateRules}
            cashback={cashback}
            isSubmitting={isSubmitting}
          />
        ),
      }}
    >
      <FormCard>
        <Field>
          <LabelRow>
            <FieldLabel htmlFor="holding-instrument">Instrument *</FieldLabel>
          </LabelRow>
          <InstrumentSearch
            id="holding-instrument"
            name="instrument"
            control={control}
            rules={{ required: "Instrument is required!" }}
            disabled={isSubmitting}
          />
          <FieldHelper>
            Search your ticker catalogue, or request a ticker that isn't in it
            yet.
          </FieldHelper>
        </Field>

        <Field>
          <LabelRow>
            <FieldLabel htmlFor="holding-quantity">Quantity *</FieldLabel>
          </LabelRow>
          <FormInput
            id="holding-quantity"
            required
            name="quantity"
            control={control}
            rules={{ validate: validateQuantity }}
            handleClear={false}
            disabled={isSubmitting}
            placeholder="e.g. 12.5"
          />
          <FieldHelper>{QUANTITY_HELPER}</FieldHelper>
        </Field>
      </FormCard>

      <FormCard>
        <Field>
          <LabelRow>
            <FieldLabel htmlFor="holding-amount">Amount *</FieldLabel>
          </LabelRow>
          <FormMoneyInput
            id="holding-amount"
            required
            name="amountEur"
            control={control}
            rules={cashbackCappedAmountRules}
            disabled={isSubmitting}
            placeholder="0.00"
          />
          <FieldHelper>{AMOUNT_HELPER}</FieldHelper>
        </Field>

        <ChargeField
          id="holding-fee"
          control={control}
          charge="fee"
          helper={FEE_HELPER}
          isSubmitting={isSubmitting}
        />

        {cashback.isShown && (
          <CashbackFundingFields
            idPrefix="holding"
            control={control}
            sourceOptions={cashback.sourceOptions}
            amountHelper={CASHBACK_BUY_AMOUNT_HELPER}
            isSubmitting={isSubmitting}
          />
        )}
      </FormCard>

      <FormCard>
        <Field>
          <LabelRow>
            <FieldLabel htmlFor="holding-date">Trade date *</FieldLabel>
          </LabelRow>
          <InputDate
            id="holding-date"
            name="date"
            control={control}
            rules={dateRules}
            disabled={isSubmitting}
            minDate={minDate}
            maxDate={localToday()}
            placeholder="YYYY/MM/DD"
          />
          <FieldHelper>
            Must be on or after the account's start date.
          </FieldHelper>
        </Field>

        <NoteField
          id="holding-note"
          control={control}
          isSubmitting={isSubmitting}
        />
      </FormCard>
    </FormPanel>
  );
};
